import { useEffect, useRef, useState } from 'react';
import { isSpeechSupported, startSpeech, type SpeechSession } from '../utils/speech';

interface Props {
  label?: string;
  listeningLabel?: string;
  onTranscript: (transcript: string) => void;
  onError?: (message: string) => void;
  disabled?: boolean;
}

export function VoiceButton({
  label = 'Speak',
  listeningLabel = 'Listening…',
  onTranscript,
  onError,
  disabled,
}: Props) {
  const [listening, setListening] = useState(false);
  const sessionRef = useRef<SpeechSession | null>(null);
  const supported = isSpeechSupported();

  useEffect(() => {
    return () => {
      sessionRef.current?.stop();
    };
  }, []);

  const handleClick = () => {
    if (!supported) {
      onError?.('Voice input not supported in this browser. Try Chrome, Edge, or Safari.');
      return;
    }
    if (listening) {
      sessionRef.current?.stop();
      return;
    }
    setListening(true);
    sessionRef.current = startSpeech({
      onResult: (transcript) => {
        onTranscript(transcript);
      },
      onError: (msg) => {
        onError?.(msg);
        setListening(false);
      },
      onEnd: () => {
        setListening(false);
        sessionRef.current = null;
      },
    });
    if (!sessionRef.current) {
      setListening(false);
    }
  };

  return (
    <button
      type="button"
      className={`voice-button${listening ? ' voice-button--listening' : ''}`}
      onClick={handleClick}
      disabled={disabled || !supported}
      title={supported ? 'Press to speak' : 'Voice input not supported in this browser'}
    >
      <span className="voice-button__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="9" y="3" width="6" height="12" rx="3" />
          <path d="M5 11a7 7 0 0 0 14 0" />
          <line x1="12" y1="18" x2="12" y2="22" />
          <line x1="8" y1="22" x2="16" y2="22" />
        </svg>
      </span>
      <span>{listening ? listeningLabel : label}</span>
    </button>
  );
}
